import React, {useState, useCallback, useEffect, useMemo} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
    Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography, TextField, InputAdornment, Switch,
    Autocomplete, Checkbox, ListItemText, MenuItem,
} from '@mui/material';
import { PhoneInput } from 'react-international-phone';
// The literal build/ path — see CreateClientDialog.comp.jsx.
import 'react-international-phone/build/index.css';
import {findCountry, getCountries, getPhoneCountries, getStatesByCountry, useGeo} from '../../utils/geo';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import {Elements} from '@stripe/react-stripe-js';
import loadStripeJs from '../../utils/loadStripe';
import {changePassword, getDQInsiderInfoService, updateProfileDetailsService, getUserDetailsService, updateUserDetailsService} from '../../services/auth.service';
import { getAvailableFeaturesService, getActiveFeaturesService, activateFeatureService, deactivateFeatureService } from '../../services/feature.service';
import {useAuth} from '../../context/auth';
import {useBranding} from '../../context/branding/branding.context';
import {useColorMode} from '../../context/colorMode/colorMode.context';
import {assignLicensesService, getOrgDetailsService, editClientService, listOrgSchoolsService} from '../../services/client.service';
import AssignLicensesDialog from './components/AssignLicensesDialog.comp';
import {
    getSubscriptionStatusService,
    cancelSubscriptionService,
    resumeSubscriptionService,
    listSavedCardsService,
    setupCardService,
    setPrimaryCardService,
    removeCardService,
    getCreditBalanceService,
    getDqtWalletService,
} from '../../services/billing.service';
import {getStripeConfigService} from '../../services_archived/app.service';
import {toast, toastWarning, getPhoneDialCode, splitPhoneByDialCode} from '../../utils/utils';
import {
    TOAST_TYPES, DQ_PRO_PLAN_KEY, DQ_PRO_PLAN_KEY_VERBOSE, USER_ACCESS_LEVEL,
    PRIMARY_ACTIVITY_CODE, PRIMARY_ACTIVITY_CODE_VERBOSE,
    ADDITIONAL_CAPABILITY_CODE, ADDITIONAL_CAPABILITY_CODE_VERBOSE,
    ORGANIZATION_TYPE_CODE, ORGANIZATION_TYPE_CODE_VERBOSE,
} from '../../utils/constants';
import CircularProgress from '@material-ui/core/CircularProgress';
import LoaderWithBackDrop from '../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import CancelSubscriptionDialog from './components/CancelSubscriptionDialog.comp';
import PaymentMethodsTab from './components/PaymentMethodsTab.comp';
import AppearanceTab from './components/AppearanceTab.comp';
import DqtWalletCard from './components/DqtWalletCard.comp';
import DqtPurchasePanel from './components/DqtPurchasePanel.comp';
import {CardElement, useStripe, useElements} from '@stripe/react-stripe-js';
import {getLearningProgramPlansService} from '../../services/learning_programs.service';
import SignupDialogV2 from './DQProV2/components/SignupDialogV2.comp.jsx';
import GuestLoginDialogV2 from './DQProV2/components/GuestLoginDialogV2.comp.jsx';
import PlansDialog from './DQProV2/components/PlansDialog.jsx';
import useUpgradeStyles from '../../assets/styles/DQProV2ProgCreatorPreviewStyles';
import TopUpCreditsDialog from './components/TopUpCreditsDialog.comp';
import DQProInvoiceList from './DQProInvoiceList.page';

const ORANGE = 'var(--brand-primary, #FF6900)';

/**
 * The four tabs the PbDQ design defines, in its order.
 *
 * Source: Navigation Bar rows 39–42 (PW_PI_ST_A / _S / _BP / _AS) and the
 * Common Section deck slides 19–22 (CS-PI-Settings-Account, -Security,
 * -Billing and Payments, and the Advanced Settings slide).
 *
 * Confirmed by Ian on 20 Aug: one Settings page with these as tabs; Advanced
 * Settings holds the dark/light toggle and nothing else; everything
 * billing-related — subscription, payment and credits/tokens — sits together
 * under Billing and Payments. Slide 21 draws the token balance on that tab.
 *
 * The deck's drawn tab label reads "Billing and Payment"; the Navigation Bar
 * and the slide code both say "Billing and Payments", which is what we use.
 */
const BILLING_TAB = 'Billing and Payments';
const ADVANCED_TAB = 'Advanced Settings';
const TAB_LABELS_BASE = ['Account', 'Security', BILLING_TAB, ADVANCED_TAB];

/**
 * Tabs the product has that the design does not place anywhere.
 *
 * Appearance (white-label branding), Licenses and Features appear in no sheet
 * and on no slide of the Settings mockup, which shows four tabs only. They are
 * kept exactly as they were rather than deleted or folded in on a guess — see
 * the open question raised with Lydia. All three are already conditional, so
 * most accounts never see them.
 */
const APPEARANCE_TAB = 'Appearance';
const LICENSES_TAB = 'Licenses';
const FEATURES_TAB = 'Features';

const reqCheck = (met) => met
    ? <CheckCircleIcon style={{fontSize: 16, color: ORANGE, marginRight: 6}}/>
    : <CancelIcon style={{fontSize: 16, color: '#D1D5DB', marginRight: 6}}/>;

const validatePassword = (pw) => ({
    length: pw.length >= 10 && pw.length <= 100,
    lowercase: /[a-z]/.test(pw),
    special: /[!@#$%^&*?.,\-_+=|;:'"]/.test(pw),
});


const isSufficientlyDifferent = (oldPw, newPw) => {
    if (!oldPw || !newPw) return false;
    return oldPw !== newPw;
};


// ─── Account Tab ─────────────────────────────────────────────────
const AccountTab = () => {
    useGeo(); // re-render with the API's lists once they load
    const {authUser, setAuthUser} = useAuth();

    // Account card state (getUserDetailsService / updateUserDetailsService)
    const [accountLoading, setAccountLoading] = useState(false);
    const [accountForm, setAccountForm] = useState({
        first_name: authUser?.first_name || '',
        last_name: authUser?.last_name || '',
        position_in_org: authUser?.position_in_org || '',
        show_insider_tutorial: false,
    });

    /*
     * Organization Info — the same fields, under the same keys, as the partner's
     * Add a School / Add a Network form (CreateClientDialog) and the network's
     * Add a school form (AddSchoolToNetworkDialog), which also match what direct
     * sales collects. An account reads the same wherever its details came from.
     */
    /*
     * Level 21 is also where a self-serve sign-up lands (default plan, no
     * organisation). That account has no school: its details are on its own
     * profile, collected at sign-up from the Powered by DQ agent's form, so it
     * reads as Organization Info like a partner's. Only an account with an
     * organisation of its own, or a direct workspace, is a school here.
     */
    const isSchoolAccount = !authUser?.is_school_network && (
        authUser?.access_level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE
        || authUser?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE
    ) && (!!authUser?.org_code || !!authUser?.is_direct_workspace);
    /*
     * The partner's own workspace (not a network, not a school). Its Organization Info
     * lists the same fields as the partner sign-up chatbot (SignupForms.comp.jsx), in
     * the same order and wording (28 Sep 2026). Networks and schools keep the full form.
     */
    const isPartnerWorkspace = !isSchoolAccount && !authUser?.is_school_network;
    const [addressLine1Error, setAddressLine1Error] = useState('');
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileForm, setProfileForm] = useState({
        org_name: authUser?.org_name || '',
        contact_first_name: authUser?.first_name || '',
        contact_last_name: authUser?.last_name || '',
        contact_title: authUser?.position_in_org || '',
        contact_email: authUser?.email || '',
        org_phone_country_code: '',
        // A network's / school's phone is its own record's, loaded below — not
        // the signed-in person's.
        org_phone_number: isPartnerWorkspace ? (authUser?.phone_number || authUser?.phone || '') : '',
        org_type_code: '',
        primary_activities: [],
        additional_capabilities: [],
        number_of_learners: '',
        org_website: '',
        org_address: '',
        org_address_line2: '',
        org_country: '',
        org_state: '',
        org_city: '',
        postal_code: '',
    });
    /*
     * PhoneInput is only seeded, never fed back from profileForm — doing that
     * fights its cursor tracking (see EditClientDialog). The seed changes once,
     * when the profile loads, and the key remounts the input with it.
     */
    const [phoneSeed, setPhoneSeed] = useState('');

    useEffect(() => {
        getUserDetailsService().then(res => {
            const u = res?.data?.user || res?.data;
            if (!u) return;
            setAccountForm(f => ({
                ...f,
                first_name:    u.first_name    || f.first_name,
                last_name:     u.last_name     || f.last_name,
                position_in_org: u.position_in_org || f.position_in_org,
                show_insider_tutorial: u.show_insider_tutorial ?? f.show_insider_tutorial,
            }));
        }).catch(() => {});

        /*
         * A network or school reads only its own record (loadOrgRecordInfo). The
         * insider profile returned by get_info is the PARTNER's, so it never fills
         * a network's or school's form — not even as a fallback, or the partner's
         * address would show (and save) as the school's.
         */
        if (!isPartnerWorkspace) {
            if (authUser?.org_code) loadOrgRecordInfo();
            return;
        }
        getDQInsiderInfoService().then(res => {
            const p = res?.data?.profile;
            if (!p) return;
            // The older flat keys (address, city, ...) are read as a fallback so
            // details saved before this form existed still show.
            const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '');
            const list = (v) => (Array.isArray(v) ? v : []);
            setProfileForm(f => ({
                ...f,
                // A partner's Organization name is stored as business_name.
                org_name:           pick(p.org_name, p.business_name, f.org_name) || '',
                contact_first_name: pick(p.contact_first_name, p.first_name, f.contact_first_name) || '',
                contact_last_name:  pick(p.contact_last_name, p.last_name, f.contact_last_name) || '',
                contact_title:      pick(p.contact_title, p.position_in_org, f.contact_title) || '',
                contact_email:      pick(p.contact_email, p.email, f.contact_email) || '',
                org_phone_country_code: pick(p.org_phone_country_code, p.phone_country_code, f.org_phone_country_code) || '',
                org_phone_number:   pick(p.org_phone_number, p.phone_number, f.org_phone_number) || '',
                // A partner's organisation type is stored on business_type (field register F014).
                org_type_code:      pick(p.org_type_code, p.business_type, f.org_type_code) || '',
                primary_activities: list(p.primary_activities),
                additional_capabilities: list(p.additional_capabilities),
                number_of_learners: pick(p.number_of_learners, f.number_of_learners) ?? '',
                org_website:        pick(p.org_website, p.website, f.org_website) || '',
                org_address:        pick(p.org_address, p.address, f.org_address) || '',
                org_address_line2:  pick(p.org_address_line2, f.org_address_line2) || '',
                org_country:        pick(p.org_country, p.office_country, p.country, f.org_country) || '',
                org_state:          pick(p.org_state, p.office_state, p.state, f.org_state) || '',
                org_city:           pick(p.org_city, p.city, f.org_city) || '',
                postal_code:        pick(p.postal_code, f.postal_code) || '',
            }));
            const code = pick(p.org_phone_country_code, p.phone_country_code);
            const number = pick(p.org_phone_number, p.phone_number);
            if (number) setPhoneSeed(code ? `${code} ${number}`.trim() : String(number));
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // The school's institute_code, once its institute row is found — sent back
    // on save so the institute fields land on the right record.
    const [instituteCode, setInstituteCode] = useState(authUser?.institute_code || '');
    /*
     * Whether the network's / school's own record has loaded. Until it has, the
     * form holds nothing of theirs, and saving it would blank their details.
     */
    const [orgRecordLoaded, setOrgRecordLoaded] = useState(false);
    // The contact email the partner entered for this account.
    const [orgContactEmail, setOrgContactEmail] = useState('');
    /*
     * Is the signed-in person this account's contact? Only then do the Account
     * card's name and title belong to the account's contact fields too — another
     * login on the same account must not rename its contact.
     */
    const isOrgContact = !!orgContactEmail
        && orgContactEmail.trim().toLowerCase() === String(authUser?.email || '').trim().toLowerCase();

    const loadOrgRecordInfo = async () => {
        const orgCode = String(authUser.org_code).trim();
        try {
            const [orgRes, schoolRows] = await Promise.all([
                getOrgDetailsService(orgCode),
                isSchoolAccount ? listOrgSchoolsService(orgCode).catch(() => []) : Promise.resolve([]),
            ]);
            const d = orgRes?.data;
            const org = (d?.org && typeof d.org === 'object') ? d.org
                : (d?.data?.org && typeof d.data.org === 'object') ? d.data.org : null;
            /*
             * The school's own institute row. A school inside a network shares the
             * network's org_code, so the list holds its sibling schools too — only
             * a row matching its institute_code is its own, or the only row there is.
             */
            const rows = schoolRows || [];
            const inst = isSchoolAccount
                ? (rows.find((r) => authUser?.institute_code && r.institute_code === authUser.institute_code)
                    || (rows.length === 1 ? rows[0] : null))
                : null;
            if (!org && !inst) return;
            if (inst?.institute_code) setInstituteCode(inst.institute_code);
            const first = (...vals) => {
                const v = vals.find((x) => x !== undefined && x !== null && x !== '');
                return v === undefined ? '' : v;
            };
            // A school's phone is on its institute row; a network's on its account.
            const phoneCode = isSchoolAccount
                ? first(inst?.institute_phone_country_code, org?.org_phone_country_code)
                : first(org?.org_phone_country_code);
            const phoneNumber = isSchoolAccount
                ? first(inst?.institute_phone_number, org?.org_phone_number)
                : first(org?.org_phone_number);
            setProfileForm((f) => ({
                ...f,
                org_name: first(org?.org_name, inst?.institute_name, f.org_name),
                org_country: first(org?.org_country),
                org_city: first(org?.org_city),
                postal_code: first(org?.postal_code),
                org_phone_country_code: phoneCode,
                org_phone_number: phoneNumber,
                ...(isSchoolAccount ? {
                    org_address: first(inst?.institute_address_line1, inst?.institute_address, org?.org_address),
                    org_address_line2: first(inst?.institute_address_line2),
                    org_state: first(inst?.institute_state),
                    org_website: first(inst?.institute_website),
                } : {
                    org_address: first(org?.org_address),
                    org_address_line2: first(org?.org_address_line2),
                    org_state: first(org?.org_state),
                    org_website: first(org?.org_website),
                }),
            }));
            if (phoneNumber) setPhoneSeed(phoneCode ? `${phoneCode} ${phoneNumber}`.trim() : String(phoneNumber));

            const contactEmail = first(org?.contact_email, inst?.institute_head_email);
            setOrgContactEmail(contactEmail);
            /*
             * The Account card shows the contact's name and title as the partner
             * entered them. Logins made before they were copied onto the User have
             * none of their own, so a blank one is filled from the account here.
             */
            if (contactEmail && contactEmail.trim().toLowerCase() === String(authUser?.email || '').trim().toLowerCase()) {
                setAccountForm((a) => ({
                    ...a,
                    first_name: a.first_name || first(org?.contact_first_name),
                    last_name: a.last_name || first(org?.contact_last_name),
                    position_in_org: a.position_in_org || (isSchoolAccount
                        ? first(inst?.institute_head_title, org?.contact_title)
                        : first(org?.contact_title)),
                }));
            }
            setOrgRecordLoaded(true);
        } catch (_) { /* keep whatever loaded; invokeApi reports the error */ }
    };

    const handleAccountChange = (e) => {
        const {name, value, type, checked} = e.target;
        setAccountForm(f => ({...f, [name]: type === 'checkbox' ? checked : value}));
    };

    const handleProfileChange = (e) => {
        const {name, value} = e.target;
        setProfileForm(f => ({...f, [name]: value}));
        if (name === 'org_address' && value.trim()) setAddressLine1Error('');
    };
    const setProfileField = (name) => (value) => setProfileForm(f => ({...f, [name]: value}));

    const handleAccountSubmit = async (e) => {
        e.preventDefault();
        setAccountLoading(true);
        try {
            const result = await updateUserDetailsService({
                first_name: accountForm.first_name,
                last_name: accountForm.last_name,
                position_in_org: accountForm.position_in_org,
                show_insider_tutorial: accountForm.show_insider_tutorial,
            });
            if (result?.data?.updated) {
                /*
                 * The account's contact is the same person — keep the contact name
                 * and title the partner sees in step with what was saved here.
                 */
                if (!isPartnerWorkspace && isOrgContact && authUser?.org_code) {
                    const first_name = accountForm.first_name;
                    const last_name = accountForm.last_name;
                    await editClientService(String(authUser.org_code).trim(), {
                        contact_first_name: first_name,
                        contact_last_name: last_name,
                        contact_name: `${first_name || ''} ${last_name || ''}`.trim(),
                        contact_title: accountForm.position_in_org,
                        ...(isSchoolAccount ? {
                            ...(instituteCode ? {institute_code: instituteCode} : {}),
                            institute_head_title: accountForm.position_in_org,
                        } : {}),
                    });
                }
                setAuthUser({...authUser, first_name: accountForm.first_name, last_name: accountForm.last_name, position_in_org: accountForm.position_in_org, show_insider_tutorial: accountForm.show_insider_tutorial});
                toast(TOAST_TYPES.SUCCESS, 'Account', 'Account details updated successfully.');
            }
        } catch (_) {}
        setAccountLoading(false);
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        // Address line 1 is required, as in the sign-up chatbot.
        if (!String(profileForm.org_address || '').trim()) {
            setAddressLine1Error('Enter address line 1');
            return;
        }
        setProfileLoading(true);
        /*
         * Network / school: save to the account's own record through the same
         * client/edit call the partner's Edit dialog uses. A school's address
         * lines, state and website go to its institute record (institute_*),
         * the rest to its account — the keys CreateClientDialog creates it with.
         */
        if (!isPartnerWorkspace && authUser?.org_code) {
            // Never save a form that holds none of the account's own details.
            if (!orgRecordLoaded) {
                setProfileLoading(false);
                toastWarning(`Your ${noun.toLowerCase()} details have not loaded yet. Please reload the page and try again.`);
                return;
            }
            const f = profileForm;
            const payload = {
                org_name: f.org_name,
                org_country: f.org_country,
                org_city: f.org_city,
                postal_code: f.postal_code,
                ...(isSchoolAccount ? {
                    ...(instituteCode ? {institute_code: instituteCode} : {}),
                    institute_address: f.org_address,
                    institute_address_line1: f.org_address,
                    institute_address_line2: f.org_address_line2,
                    institute_state: f.org_state,
                    institute_website: f.org_website,
                    institute_phone_country_code: f.org_phone_country_code,
                    institute_phone_number: f.org_phone_number,
                } : {
                    org_address: f.org_address,
                    org_address_line2: f.org_address_line2,
                    org_state: f.org_state,
                    org_website: f.org_website,
                    org_phone_country_code: f.org_phone_country_code,
                    org_phone_number: f.org_phone_number,
                }),
            };
            try {
                const result = await editClientService(String(authUser.org_code).trim(), payload);
                if (result?.data?.success !== false) {
                    setAuthUser({...authUser, org_name: f.org_name});
                    toast(TOAST_TYPES.SUCCESS, 'Account', `${noun} info updated successfully.`);
                }
            } catch (_) {}
            setProfileLoading(false);
            return;
        }
        try {
            const f = profileForm;
            const result = await updateProfileDetailsService({
                org_name: f.org_name,
                primary_activities: f.primary_activities,
                office_address: f.org_address,
                office_city: f.org_city,
                office_postal_code: f.postal_code,
                office_state: f.org_state,
                // `country` is a billing key on this endpoint — the office
                // country travels as office_country, like office_state.
                office_country: f.org_country,
                website: f.org_website,
                business_type: f.org_type_code,
                phone_country_code: f.org_phone_country_code,
                phone_number: f.org_phone_number,
            });
            if (result?.data?.updated) {
                setAuthUser({...authUser, org_name: profileForm.org_name});
                toast(TOAST_TYPES.SUCCESS, 'Account', 'Profile updated successfully.');
            }
        } catch (_) {}
        setProfileLoading(false);
    };

    /*
     * One fixed height for every input on the page. Text fields, the Country /
     * State Autocompletes and the Primary role multi-select each come with
     * MUI's own padding, so without this they render at three different
     * heights (PW / SNW / SW review, 23 Sep).
     */
    const inputSx = {
        '& .MuiOutlinedInput-root': {
            height: '44px',
            boxSizing: 'border-box',
            borderRadius: '8px',
            backgroundColor: 'var(--brand-surface, #F9FAFB)',
            fontSize: '0.9rem',
            '& fieldset': {borderColor: 'var(--brand-border, #E5E7EB)'},
            '&:hover fieldset': {borderColor: '#D1D5DB'},
            '&.Mui-focused fieldset': {borderColor: '#9CA3AF'},
            // A disabled field (State, until a country is picked) otherwise gets
            // MUI's darker default outline and reads as a bigger, different box.
            '&.Mui-disabled fieldset': {borderColor: 'var(--brand-border, #E5E7EB)'},
        },
        '& .MuiOutlinedInput-input': {py: '10px', px: '14px'},
        // Autocomplete pads its root and its input on top of the above.
        '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {py: 0, pl: 0},
        '& .MuiAutocomplete-inputRoot .MuiOutlinedInput-input.MuiAutocomplete-input': {py: '10px', px: '14px'},
        // The multi-select's displayed value sits in its own box.
        '& .MuiSelect-select': {display: 'flex', alignItems: 'center', minHeight: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'},
    };

    const labelSx = {fontSize: '0.85rem', fontWeight: 600, color: 'var(--brand-text, #111827)', mb: 0.75};
    const helperSx = {fontSize: '0.78rem', color: 'var(--brand-text-muted, #6B7280)', mt: 0.5};
    const cardSx = {border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', p: {xs: 2.5, sm: 4}};
    const saveBtnSx = {
        backgroundColor: '#1F2A37', color: '#fff', textTransform: 'none',
        fontWeight: 600, fontSize: '0.9rem', px: 3, py: 1.1, borderRadius: '8px',
        '&:hover': {backgroundColor: '#111827'},
    };

    /*
     * Organization Info fields — the sign-up chatbot's list, order and wording, per
     * workspace (28 Sep 2026):
     *   partner  — Organization name, Primary role / activity, country, address, website
     *   network  — Organization name, country, address, website (optional)
     *   school   — the same as a network, worded "School"
     * Address line 1 is required for all three.
     */
    const noun = isSchoolAccount ? 'School' : 'Organization';
    /*
     * Fields not on those lists, switched off for now rather than deleted: contact
     * first / last name, title, contact email, the partner's phone number,
     * organization type, primary role / activity (network and school), additional
     * capabilities, number of learners. Saved values are kept and still sent on
     * save. Set true to restore.
     *
     * A network's / school's phone IS shown (below the postal code), and its
     * contact's name, title and email appear in the Account card — they are what
     * the partner entered when creating it (12 Oct 2026).
     */
    const SHOW_EXTRA_ORG_FIELDS = false;
    const fullRow = {gridColumn: '1 / -1'};
    const stateCountryCode = String(profileForm.org_state || '').split('-')[0];
    const profileCountry = findCountry(profileForm.org_country)
        || (profileForm.org_state ? findCountry(stateCountryCode) : undefined);
    const statesForCountry = getStatesByCountry(profileCountry?.code || '');

    const orgNameField = (
        <Box>
            <Typography sx={labelSx}>{noun} name</Typography>
            <TextField fullWidth name="org_name" autoComplete="organization" value={profileForm.org_name} onChange={handleProfileChange} sx={inputSx}/>
        </Box>
    );
    const primaryRoleField = (
        <Box>
            <Typography sx={labelSx}>Primary role / activity</Typography>
            <TextField
                select fullWidth name="primary_activities" value={profileForm.primary_activities} onChange={handleProfileChange} sx={inputSx}
                SelectProps={{multiple: true, renderValue: (selected) => selected.map((code) => PRIMARY_ACTIVITY_CODE_VERBOSE[code]).join(', ')}}
            >
                {Object.values(PRIMARY_ACTIVITY_CODE).map((code) => (
                    <MenuItem key={code} value={code}>
                        <Checkbox checked={profileForm.primary_activities.indexOf(code) > -1}/>
                        <ListItemText primary={PRIMARY_ACTIVITY_CODE_VERBOSE[code]}/>
                    </MenuItem>
                ))}
            </TextField>
        </Box>
    );
    const orgTypeField = (
        <Box>
            <Typography sx={labelSx}>Organization type</Typography>
            <TextField select fullWidth name="org_type_code" value={profileForm.org_type_code} onChange={handleProfileChange} sx={inputSx}>
                {Object.values(ORGANIZATION_TYPE_CODE).map((code) => (
                    <MenuItem key={code} value={code}>{ORGANIZATION_TYPE_CODE_VERBOSE[code]}</MenuItem>
                ))}
            </TextField>
        </Box>
    );
    const countryField = (
        <Box>
            <Typography sx={labelSx}>{noun} country</Typography>
            <Autocomplete
                options={getCountries()}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => option.code === value?.code}
                // Older profiles may hold the country's name rather than its code.
                value={profileCountry || null}
                onChange={(event, newValue) => {
                    // The state list is keyed by country — clear a stale state.
                    setProfileForm(f => ({...f, org_country: newValue?.code || '', org_state: ''}));
                }}
                renderInput={(params) => <TextField {...params} placeholder="Select country" sx={inputSx}/>}
            />
        </Box>
    );
    const addressLine1Field = (
        <Box sx={fullRow}>
            {/* Required, but no "*" — only optional fields are marked, as "(optional)". */}
            <Typography sx={labelSx}>Address line 1</Typography>
            <TextField
                fullWidth name="org_address" placeholder="Street address" value={profileForm.org_address} onChange={handleProfileChange} sx={inputSx}
                error={!!addressLine1Error}
                helperText={addressLine1Error || undefined}
            />
        </Box>
    );
    const addressLine2Field = (
        <Box sx={fullRow}>
            <Typography sx={labelSx}>Address line 2 (optional)</Typography>
            <TextField fullWidth name="org_address_line2" placeholder="Apartment, suite, building" value={profileForm.org_address_line2} onChange={handleProfileChange} sx={inputSx}/>
        </Box>
    );
    const cityField = (
        <Box>
            <Typography sx={labelSx}>City</Typography>
            <TextField fullWidth name="org_city" placeholder="City" value={profileForm.org_city} onChange={handleProfileChange} sx={inputSx}/>
        </Box>
    );
    const stateField = (
        <Box>
            <Typography sx={labelSx}>State, province or region</Typography>
            <Autocomplete
                options={statesForCountry}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.code === value?.code}
                value={statesForCountry.find((st) => st.code === profileForm.org_state
                    || (st.name && st.name.toLowerCase() === String(profileForm.org_state || '').toLowerCase())) || null}
                onChange={(event, newValue) => setProfileField('org_state')(newValue ? newValue.code : '')}
                disabled={!profileCountry || statesForCountry.length === 0}
                renderInput={(params) => (
                    <TextField {...params} sx={inputSx}
                        placeholder={!profileCountry ? 'Select country first' : statesForCountry.length === 0 ? 'N/A' : 'Select state'}/>
                )}
            />
        </Box>
    );
    const postalCodeField = (
        <Box>
            <Typography sx={labelSx}>Postal code</Typography>
            <TextField fullWidth name="postal_code" placeholder="Postal code" value={profileForm.postal_code} onChange={handleProfileChange} sx={inputSx}/>
        </Box>
    );
    const phoneField = (
        <Box>
            <Typography sx={labelSx}>{noun} phone number</Typography>
            <PhoneInput
                countries={getPhoneCountries()}
                key={phoneSeed || 'profile-phone'}
                value={phoneSeed}
                defaultCountry="us"
                disableDialCodePrefill
                onChange={(phone, countryIso2) => {
                    if (!phone) {
                        setProfileForm(f => ({...f, org_phone_country_code: '', org_phone_number: ''}));
                        return;
                    }
                    const dialCode = getPhoneDialCode(countryIso2);
                    setProfileForm(f => ({
                        ...f,
                        org_phone_country_code: dialCode,
                        org_phone_number: splitPhoneByDialCode(phone, dialCode),
                    }));
                }}
                inputProps={{name: 'org_phone_number', autoComplete: 'tel'}}
                inputStyle={{flex: 1, minWidth: 0}}
                style={{
                    width: '100%',
                    '--react-international-phone-height': '44px',
                    '--react-international-phone-border-color': 'var(--brand-border, #E5E7EB)',
                }}
            />
        </Box>
    );
    const websiteField = (
        <Box>
            {/* Optional for every workspace — the partner sign-up chatbot doesn't require it either. */}
            <Typography sx={labelSx}>{noun} website (optional)</Typography>
            <TextField fullWidth name="org_website" placeholder="https://example.com" autoComplete="url" value={profileForm.org_website} onChange={handleProfileChange} sx={inputSx}/>
            <Typography sx={helperSx}>
                {isSchoolAccount
                    ? 'If your school does not have a website, you may provide a public school profile.'
                    : 'If your organization does not have a website, you may provide a public organization profile or professional profile.'}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>

            {/* ── Account card ─────────────────────────────────── */}
            <Box sx={cardSx}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 3}}>
                    <Typography sx={{fontSize: '1.15rem', fontWeight: 700, color: 'var(--brand-text, #111827)'}}>Account</Typography>
                    <InfoOutlinedIcon style={{fontSize: 16, color: '#9CA3AF'}}/>
                </Box>

                <form onSubmit={handleAccountSubmit}>
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, columnGap: 3, rowGap: 2.5}}>
                        <Box>
                            <Typography sx={labelSx}>First name</Typography>
                            <TextField fullWidth name="first_name" placeholder="Your first name" value={accountForm.first_name} onChange={handleAccountChange} sx={inputSx}/>
                        </Box>
                        <Box>
                            <Typography sx={labelSx}>Last name</Typography>
                            <TextField fullWidth name="last_name" placeholder="Your last name" value={accountForm.last_name} onChange={handleAccountChange} sx={inputSx}/>
                        </Box>
                        <Box>
                            <Typography sx={labelSx}>Title / role (optional)</Typography>
                            <TextField fullWidth name="position_in_org" placeholder={`Your role in the ${noun.toLowerCase()}`} value={accountForm.position_in_org} onChange={handleAccountChange} sx={inputSx}/>
                        </Box>
                        {/* The sign-in email — for a network or school the contact email
                            the partner entered. Shown, not editable. */}
                        {(
                            <Box>
                                <Typography sx={labelSx}>Email</Typography>
                                <TextField fullWidth name="contact_email" type="email" value={orgContactEmail || authUser?.email || ''} disabled sx={inputSx}/>
                                <Typography sx={helperSx}>Your sign-in email. It can't be changed here.</Typography>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{
                        mt: 2.5, display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', p: '12px 16px',
                        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '8px', backgroundColor: 'var(--brand-surface, #F9FAFB)',
                    }}>
                        <Box>
                            <Typography sx={{fontSize: '0.875rem', fontWeight: 600, color: 'var(--brand-text, #111827)'}}>Show Tutorial</Typography>
                            <Typography sx={{fontSize: '0.78rem', color: 'var(--brand-text-muted, #6B7280)'}}>Display tutorial hints and guides throughout the app</Typography>
                        </Box>
                        <Switch
                            name="show_insider_tutorial"
                            checked={accountForm.show_insider_tutorial}
                            onChange={handleAccountChange}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {color: '#1F2A37'},
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {backgroundColor: '#1F2A37'},
                            }}
                        />
                    </Box>

                    <Box sx={{borderTop: '1px solid var(--brand-border, #E5E7EB)', mt: 3.5, pt: 2.5}}>
                        <Button type="submit" variant="contained" disabled={accountLoading} sx={saveBtnSx}>
                            {accountLoading ? 'Saving...' : 'Save changes'}
                        </Button>
                    </Box>
                </form>
                <LoaderWithBackDrop loading={accountLoading}/>
            </Box>

            {/* ── Insider Profile card ──────────────────────────── */}
            <Box sx={cardSx}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 3}}>
                    <Typography sx={{fontSize: '1.15rem', fontWeight: 700, color: 'var(--brand-text, #111827)'}}>{noun} Info</Typography>
                    <InfoOutlinedIcon style={{fontSize: 16, color: '#9CA3AF'}}/>
                </Box>

                <form onSubmit={handleProfileSubmit}>
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, columnGap: 3, rowGap: 2.5}}>
                        {orgNameField}
                        {/* Partner: the agent's organisation fields, in its order. */}
                        {isPartnerWorkspace && orgTypeField}
                        {isPartnerWorkspace && primaryRoleField}
                        {countryField}
                        <Typography sx={{...labelSx, ...fullRow, mb: -1}}>{noun} address</Typography>
                        {addressLine1Field}
                        {/* Networks and schools only: the partner profile has one
                            address field (sign-up joins line 2 onto it). */}
                        {!isPartnerWorkspace && addressLine2Field}
                        {cityField}
                        {stateField}
                        {postalCodeField}
                        {phoneField}
                        {websiteField}
                        {SHOW_EXTRA_ORG_FIELDS && (
                            <>
                                <Box>
                                    <Typography sx={labelSx}>Contact first name</Typography>
                                    <TextField fullWidth name="contact_first_name" placeholder="Bonnie" value={profileForm.contact_first_name} onChange={handleProfileChange} sx={inputSx}/>
                                </Box>
                                <Box>
                                    <Typography sx={labelSx}>Contact last name</Typography>
                                    <TextField fullWidth name="contact_last_name" placeholder="Green" value={profileForm.contact_last_name} onChange={handleProfileChange} sx={inputSx}/>
                                </Box>
                                <Box>
                                    <Typography sx={labelSx}>Title / role (optional)</Typography>
                                    <TextField fullWidth name="contact_title" placeholder="e.g. Program Manager" value={profileForm.contact_title} onChange={handleProfileChange} sx={inputSx}/>
                                </Box>
                                <Box>
                                    <Typography sx={labelSx}>Contact email</Typography>
                                    <TextField fullWidth name="contact_email" placeholder="name@example.com" type="email" value={profileForm.contact_email} onChange={handleProfileChange} sx={inputSx}/>
                                </Box>
                                <Box>
                                    <Typography sx={labelSx}>Organization type</Typography>
                                    <TextField select fullWidth name="org_type_code" value={profileForm.org_type_code} onChange={handleProfileChange} sx={inputSx}>
                                        {Object.values(ORGANIZATION_TYPE_CODE).map((code) => (
                                            <MenuItem key={code} value={code}>{ORGANIZATION_TYPE_CODE_VERBOSE[code]}</MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                {!isPartnerWorkspace && primaryRoleField}
                                <Box>
                                    <Typography sx={labelSx}>Additional capabilities</Typography>
                                    <TextField
                                        select fullWidth name="additional_capabilities" value={profileForm.additional_capabilities} onChange={handleProfileChange} sx={inputSx}
                                        SelectProps={{multiple: true, renderValue: (selected) => selected.map((code) => ADDITIONAL_CAPABILITY_CODE_VERBOSE[code]).join(', ')}}
                                    >
                                        {Object.values(ADDITIONAL_CAPABILITY_CODE).map((code) => (
                                            <MenuItem key={code} value={code}>
                                                <Checkbox checked={profileForm.additional_capabilities.indexOf(code) > -1}/>
                                                <ListItemText primary={ADDITIONAL_CAPABILITY_CODE_VERBOSE[code]}/>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                {isSchoolAccount && (
                                    <Box>
                                    <Typography sx={labelSx}>Number of learners</Typography>
                                    <TextField fullWidth name="number_of_learners" type="number" value={profileForm.number_of_learners} onChange={handleProfileChange} sx={inputSx}/>
                                </Box>
                                )}
                            </>
                        )}
                    </Box>

                    <Box sx={{borderTop: '1px solid var(--brand-border, #E5E7EB)', mt: 3.5, pt: 2.5}}>
                        <Button type="submit" variant="contained" disabled={profileLoading} sx={saveBtnSx}>
                            {profileLoading ? 'Saving...' : 'Save changes'}
                        </Button>
                    </Box>
                </form>
                <LoaderWithBackDrop loading={profileLoading}/>
            </Box>

        </Box>
    );
};

// ─── Security Tab ────────────────────────────────────────────────
const SecurityTab = () => {
    const {authUser, setAuthUser} = useAuth();
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({old_password: '', new_password: '', confirm_new_password: ''});
    const [show, setShow] = useState({old_password: false, new_password: false, confirm_new_password: false});
    const checks = {
        ...validatePassword(form.new_password),
        different: isSufficientlyDifferent(form.old_password, form.new_password),
    };

    const toggleShow = (field) => setShow(s => ({...s, [field]: !s[field]}));
    const visibilityAdornment = (field) => ({
        endAdornment: (
            <InputAdornment position="end">
                <IconButton
                    aria-label={`toggle ${field} visibility`}
                    onClick={() => toggleShow(field)}
                    edge="end"
                    size="small"
                    tabIndex={-1}
                >
                    {show[field] ? <VisibilityOff fontSize="small"/> : <Visibility fontSize="small"/>}
                </IconButton>
            </InputAdornment>
        ),
    });

    const handleChange = (e) => {
        const {name, value} = e.target;
        setForm(f => ({...f, [name]: value}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.old_password || !form.new_password || !form.confirm_new_password) {
            return toastWarning('Please fill in all password fields!');
        }
        if (form.new_password !== form.confirm_new_password) {
            return toastWarning('Please provide the same password in both password fields!');
        }
        if (!isSufficientlyDifferent(form.old_password, form.new_password)) {
            return toastWarning('Your new password must be significantly different from your current password.');
        }
        setLoading(true);
        try {
            const result = await changePassword(form.old_password, form.new_password);
            if (result?.data?.updated_password) {
                setAuthUser({
                    ...authUser,
                    required_password_reset: false,
                });
                toast(TOAST_TYPES.SUCCESS, 'Password Changed', 'Successfully Changed Password');
                setForm({old_password: '', new_password: '', confirm_new_password: ''});
            }
        } catch (_) {
            // invokeApi already surfaces errors
        }
        setLoading(false);
    };

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: 'var(--brand-surface, #fff)',
            '& fieldset': {borderColor: 'var(--brand-border, #E5E7EB)'},
            '&:hover fieldset': {borderColor: ORANGE},
            '&.Mui-focused fieldset': {borderColor: ORANGE},
        },
    };

    return (
        <Box sx={{display: 'flex', gap: 4, flexWrap: 'wrap'}}>
            {/* Left: form */}
            <Box sx={{flex: '1 1 400px', background: 'var(--brand-surface, #fff)', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '16px', p: '32px 36px'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 3}}>
                    <Typography sx={{fontSize: 22, fontWeight: 700, color: 'var(--brand-text, #111827)'}}>Password</Typography>
                    <InfoOutlinedIcon style={{fontSize: 18, color: '#9CA3AF'}}/>
                </Box>

                <form onSubmit={handleSubmit}>
                    <Typography sx={{fontSize: 14, fontWeight: 500, color: 'var(--brand-text, #374151)', mb: 0.75}}>Enter current password</Typography>
                    <TextField
                        fullWidth name="old_password" type={show.old_password ? 'text' : 'password'}
                        placeholder="Enter current password"
                        value={form.old_password} onChange={handleChange}
                        sx={{...inputSx, mb: 2.5}} size="small"
                        InputProps={visibilityAdornment('old_password')}
                    />

                    <Typography sx={{fontSize: 14, fontWeight: 500, color: 'var(--brand-text, #374151)', mb: 0.75}}>Enter new password</Typography>
                    <TextField
                        fullWidth name="new_password" type={show.new_password ? 'text' : 'password'}
                        placeholder="Enter new password"
                        value={form.new_password} onChange={handleChange}
                        sx={{...inputSx, mb: 2.5}} size="small"
                        InputProps={visibilityAdornment('new_password')}
                    />

                    <Typography sx={{fontSize: 14, fontWeight: 500, color: 'var(--brand-text, #374151)', mb: 0.75}}>Confirm new password</Typography>
                    <TextField
                        fullWidth name="confirm_new_password" type={show.confirm_new_password ? 'text' : 'password'}
                        placeholder="Confirm new password"
                        value={form.confirm_new_password} onChange={handleChange}
                        sx={{...inputSx, mb: 3}} size="small"
                        InputProps={visibilityAdornment('confirm_new_password')}
                    />

                    <Button type="submit" variant="contained"
                        sx={{background: '#111827', color: '#fff', borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, py: 1.25,
                            '&:hover': {background: '#1F2937'}}}>
                        Save changes
                    </Button>
                </form>
            </Box>

            {/* Right: requirements */}
            <Box sx={{flex: '0 1 320px', background: 'var(--brand-surface, #F9FAFB)', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '16px', p: '32px 28px', alignSelf: 'flex-start'}}>
                <Typography sx={{fontSize: 15, fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 0.5}}>Password requirements:</Typography>
                <Typography sx={{fontSize: 13, color: 'var(--brand-text-muted, #6B7280)', mb: 2}}>Ensure that these requirements are met:</Typography>
                {[
                    [checks.length, 'At least 10 characters (and up to 100 characters)'],
                    [checks.lowercase, 'At least one lowercase character'],
                    [checks.special, 'Inclusion of at least one special character, e.g., ! @ # ? . , - _'],
                    [checks.different, 'Significantly different from your previous passwords'],
                ].map(([met, text], i) => (
                    <Box key={i} sx={{display: 'flex', alignItems: 'flex-start', mb: 1}}>
                        {reqCheck(met)}
                        <Typography sx={{fontSize: 13, color: 'var(--brand-text, #374151)', lineHeight: 1.5}}>{text}</Typography>
                    </Box>
                ))}
            </Box>

            <LoaderWithBackDrop loading={loading}/>
        </Box>
    );
};

// ─── Subscription Plan Tab ───────────────────────────────────────
const parseFeatures = (features = []) => {
    const sourceLines = Array.isArray(features)
        ? features.flatMap(e => String(e || '').split(/\r?\n/))
        : String(features || '').split(/\r?\n/);
    const sections = [];
    let current = null;
    sourceLines.forEach(line => {
        if (typeof line !== 'string') return;
        const norm = line.trim().replace(/^•\s*/, '- ');
        if (!norm) return;
        const heading = norm.match(/^[-*]?\s*#+\s*(.+)$/);
        if (heading) {
            current = {title: heading[1].trim(), items: []};
            sections.push(current);
        } else if (/^[-*]\s+/.test(norm)) {
            if (current) current.items.push(norm.replace(/^[-*]\s+/, '').trim());
        } else {
            if (!current) { current = {title: '', items: []}; sections.push(current); }
            current.items.push(norm);
        }
    });
    return sections;
};

const SubscriptionTab = () => {
    const upgradeClasses = useUpgradeStyles();
    const { authUser } = useAuth();
    const [actionLoading, setActionLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [planLabel, setPlanLabel] = useState('');
    const [planKey, setPlanKey] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
    const [periodEndDate, setPeriodEndDate] = useState('-');
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [cancelMode, setCancelMode] = useState('period_end');
    const [planPrice, setPlanPrice] = useState('');
    const [planSubtitle, setPlanSubtitle] = useState('');
    const [featureSections, setFeatureSections] = useState([]);
    const [showPlans, setShowPlans] = useState(false);
    const [showSignupDialog, setShowSignupDialog] = useState(false);
    const [showLoginDialog, setShowLoginDialog] = useState(false);

    useEffect(() => {
        const load = async () => {
            setDataLoading(true);
            try {
                // 1. fetch subscription status
                const subRes = await getSubscriptionStatusService();
                let currentPlanKey = DQ_PRO_PLAN_KEY.DEFAULT;
                if (subRes?.data) {
                    const d = subRes.data;
                    setPlanLabel(d.plan_label || '');
                    currentPlanKey = d.plan_key || DQ_PRO_PLAN_KEY.DEFAULT;
                    setPlanKey(currentPlanKey);
                    setCancelAtPeriodEnd(d.cancel_at_period_end || false);
                    setSubscriptionStatus(
                        d.cancel_at_period_end && d.subscription_status === 'active'
                            ? 'cancel_at_period_end'
                            : d.subscription_status
                    );
                    if (d.subscription_renews_at) {
                        const dt = new Date(d.subscription_renews_at);
                        setPeriodEndDate(dt.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'}));
                    }
                }

                // 2. fetch plans using the resolved key
                const plansRes = await getLearningProgramPlansService();
                const plans = plansRes?.data?.plans || plansRes?.data || [];
                // current_plan carries the user's actual plan (incl. enterprise tiers that aren't in
                // the public `plans` catalog) — prefer it so its features/price render correctly.
                const currentPlan = plansRes?.data?.current_plan;
                const match = currentPlan
                    || plans.find(p => p.key === currentPlanKey || p.plan_key === currentPlanKey)
                    || plans[0];
                if (match) {
                    if (match?.price_monthly_usd) setPlanPrice(`$${match.price_monthly_usd}`);
                    else if (match?.price) setPlanPrice(`$${match.price}`);
                    if (match?.description) setPlanSubtitle(match.description);
                    else if (match?.subtitle) setPlanSubtitle(match.subtitle);
                    setFeatureSections(parseFeatures(match?.features || []));
                }
            } catch (_) {}
            setDataLoading(false);
        };
        load();
    }, []);

    const handleConfirmCancel = async () => {
        setActionLoading(true);
        try {
            await cancelSubscriptionService(cancelMode === 'immediate');
            setDataLoading(true);
            const res = await getSubscriptionStatusService();
            if (res?.data) {
                const d = res.data;
                setPlanLabel(d.plan_label || '');
                setSubscriptionStatus(d.cancel_at_period_end && d.subscription_status === 'active' ? 'cancel_at_period_end' : d.subscription_status);
            }
        } catch (_) {}
        setActionLoading(false);
        setDataLoading(false);
        setCancelModalOpen(false);
    };

    const fetchSubscriptionData = async () => {
        const res = await getSubscriptionStatusService();
        if (res?.data) {
            const d = res.data;
            setPlanLabel(d.plan_label || '');
            const key = d.plan_key || DQ_PRO_PLAN_KEY.DEFAULT;
            setPlanKey(key);
            setCancelAtPeriodEnd(d.cancel_at_period_end || false);
            setSubscriptionStatus(
                d.cancel_at_period_end && d.subscription_status === 'active'
                    ? 'cancel_at_period_end'
                    : d.subscription_status
            );
            if (d.subscription_renews_at) {
                const dt = new Date(d.subscription_renews_at);
                setPeriodEndDate(dt.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'}));
            }
        }
    };

    const handleResumeSubscription = async () => {
        setActionLoading(true);
        try {
            await resumeSubscriptionService();
            await fetchSubscriptionData();
        } catch (_) {}
        setActionLoading(false);
    };

    const isActive = ['active', 'trialing', 'past_due', 'incomplete'].includes(subscriptionStatus);
    const SHOW_CANCEL = process.env.REACT_APP_SHOW_CANCEL_SUBSCRIPTION !== 'false';

    if (dataLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300}}>
                <CircularProgress style={{color: ORANGE}}/>
            </Box>
        );
    }

    return (
        <Box sx={{display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'stretch'}}>
            {/* Left: plan card */}
            <Box sx={{
                flex: '0 1 400px',
                background: 'var(--brand-surface, #fff)',
                border: '1px solid var(--brand-border, #E5E7EB)',
                borderRadius: '20px',
                p: '32px 28px',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Decorative top stripe */}
                <Box sx={{position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: ORANGE, borderRadius: '20px 20px 0 0'}}/>

                <Typography sx={{fontSize: 28, fontWeight: 800, color: 'var(--brand-text, #111827)', lineHeight: 1.1, mb: 0.75}}>
                    {planLabel || 'Current Plan'}
                </Typography>

                {planSubtitle && (
                    <Typography sx={{fontSize: 14, color: 'var(--brand-text-muted, #6B7280)', mb: 2.5, lineHeight: 1.5}}>
                        {planSubtitle}
                    </Typography>
                )}

                <Box sx={{mb: 3}}>
                    {(!isActive || planKey === DQ_PRO_PLAN_KEY.DEFAULT) && (
                        <Typography sx={{fontSize: 13, color: '#9CA3AF', fontWeight: 500, letterSpacing: '0.5px', mb: 0.5}}>
                            Starting at
                        </Typography>
                    )}
                    <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.5}}>
                        <Typography sx={{fontSize: 44, fontWeight: 900, color: 'var(--brand-text, #111827)', lineHeight: 1}}>
                            {planPrice || '—'}
                        </Typography>
                        {planPrice && (
                            <Typography sx={{fontSize: 14, color: '#9CA3AF', fontWeight: 500}}>/month</Typography>
                        )}
                    </Box>
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.25, mt: 'auto'}}>
                    {(!isActive || planKey === DQ_PRO_PLAN_KEY.DEFAULT) && (
                        <Button fullWidth variant="contained"
                            onClick={() => {
                                if (!authUser || authUser.is_guest) setShowSignupDialog(true);
                                else setShowPlans(true);
                            }}
                            sx={{background: ORANGE, color: '#fff', borderRadius: '12px', textTransform: 'none',
                                fontWeight: 700, py: 1.25, fontSize: 14, boxShadow: 'none',
                                '&:hover': {background: '#e55e00', boxShadow: '0 4px 12px rgba(255,105,0,0.3)'}}}>
                            Upgrade plan
                        </Button>
                    )}

                    {SHOW_CANCEL && isActive && planKey !== DQ_PRO_PLAN_KEY.DEFAULT && (
                        <Button fullWidth variant="outlined"
                                onClick={() => setCancelModalOpen(true)}
                                sx={{borderColor: 'var(--brand-border, #E5E7EB)', color: 'var(--brand-text-muted, #6B7280)', borderRadius: '12px',
                                    textTransform: 'none', fontWeight: 600, py: 1.25, fontSize: 14,
                                    '&:hover': {borderColor: '#EF4444', color: '#EF4444', background: '#FEF2F2'}}}>
                            Cancel subscription
                        </Button>
                    )}

                    {subscriptionStatus === 'cancel_at_period_end' && (
                        <Button fullWidth variant="contained" onClick={handleResumeSubscription}
                            sx={{background: ORANGE, color: '#fff', borderRadius: '12px', textTransform: 'none',
                                fontWeight: 700, py: 1.25, fontSize: 14, boxShadow: 'none',
                                '&:hover': {background: '#e55e00'}}}>
                            Resume Subscription
                        </Button>
                    )}

                    {subscriptionStatus === 'cancelled' && (
                        <Button fullWidth variant="contained" onClick={() => setShowPlans(true)}
                            sx={{background: ORANGE, color: '#fff', borderRadius: '12px', textTransform: 'none',
                                fontWeight: 700, py: 1.25, fontSize: 14, boxShadow: 'none',
                                '&:hover': {background: '#e55e00'}}}>
                            Resubscribe
                        </Button>
                    )}

                    {periodEndDate !== '-' && (
                        <Typography sx={{fontSize: 13, color: '#9CA3AF', textAlign: 'center', mt: 0.5}}>
                            {cancelAtPeriodEnd ? 'Access until' : 'Renews'} {periodEndDate}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Right: features — temporarily removed from Billing and Payments per
                request. Kept mounted-but-disabled (false &&) rather than deleted,
                same pattern used elsewhere in this codebase for an easy revert. */}
            {false && (
            <Box sx={{
                flex: 1, minWidth: 280,
                background: 'var(--brand-surface, #fff)',
                border: '1px solid var(--brand-border, #E5E7EB)',
                borderRadius: '20px',
                p: '32px 32px',
            }}>
                {featureSections.length === 0 ? (
                    <Typography sx={{color: '#9CA3AF', fontSize: 14}}>No features available.</Typography>
                ) : featureSections.filter(s => !/flexibility/i.test(s.title)).map((section, i, arr) => (
                    <Box key={i} sx={{mb: i < featureSections.length - 1 ? 3 : 0}}>
                        {section.title && (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5}}>
                                <Box sx={{width: 3, height: 18, background: ORANGE, borderRadius: 2}}/>
                                <Typography sx={{fontSize: 15, fontWeight: 800, color: 'var(--brand-text, #111827)',
                                    textTransform: 'uppercase', letterSpacing: '0.8px'}}>
                                    {section.title}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                            {section.items.map((item, j) => (
                                <Box key={j} sx={{display: 'flex', alignItems: 'flex-start', gap: 1.25,
                                    p: '0 12px', borderRadius: '10px', '&:hover': {background: 'var(--brand-surface, #F9FAFB)'}}}>
                                    <Box sx={{width: 20, height: 20, borderRadius: '50%', background: 'rgba(255,105,0,0.1)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '1px'}}>
                                        <CheckIcon style={{fontSize: 13, color: ORANGE}}/>
                                    </Box>
                                    <Typography sx={{fontSize: 15, color: 'var(--brand-text, #374151)', lineHeight: 1.6}}>{item}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {i < arr.length - 1 && (
                            <Box sx={{height: '1px', background: 'var(--brand-border, #F3F4F6)', mt: 2.5}}/>
                        )}
                    </Box>
                ))}
            </Box>
            )}

            <CancelSubscriptionDialog
                open={cancelModalOpen}
                onClose={() => setCancelModalOpen(false)}
                cancelMode={cancelMode}
                setCancelMode={setCancelMode}
                periodEndDate={periodEndDate}
                handleConfirmCancel={handleConfirmCancel}
            />

            <PlansDialog
                open={showPlans}
                onClose={() => setShowPlans(false)}
                onSelectPlan={(selectedPlanKey) => {
                    setPlanKey(selectedPlanKey);
                    fetchSubscriptionData();
                }}
                currentPlanKey={planKey}
                currentPlanTitle={planLabel}
                classes={upgradeClasses}
                forceNewSubscription={subscriptionStatus === 'cancelled'}
            />

            <SignupDialogV2
                open={showSignupDialog}
                onClose={() => setShowSignupDialog(false)}
                onLoginClick={() => {
                    setShowSignupDialog(false);
                    setShowLoginDialog(true);
                }}
                onSuccess={() => {
                    setShowSignupDialog(false);
                    setShowPlans(true);
                }}
            />

            <GuestLoginDialogV2
                open={showLoginDialog}
                onClose={() => setShowLoginDialog(false)}
                onSignupClick={() => {
                    setShowLoginDialog(false);
                    setShowSignupDialog(true);
                }}
                onSuccess={() => {
                    setShowLoginDialog(false);
                    setShowPlans(true);
                }}
            />

            <LoaderWithBackDrop loading={actionLoading}/>
        </Box>
    );
};

// ─── Add Card Form (must be outside PaymentTab to avoid Stripe iframe remount) ──
const AddCardForm = ({cardSetupSecret, onSuccess, onClose}) => {
    const stripe = useStripe();
    const elements = useElements();
    // Stripe CardElement runs in a sandboxed iframe — CSS variables are not resolved there.
    // Read the current mode synchronously from the attribute set by ColorModeProvider.
    const isDark = document.documentElement.getAttribute('data-color-mode') === 'dark';
    const stripeTextColor = isDark ? '#E5E7EB' : '#1e293b';
    const stripePlaceholderColor = isDark ? '#6B7280' : '#94a3b8';
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [cardholderName, setCardholderName] = useState('');
    const submittingRef = React.useRef(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || !cardSetupSecret) return;
        if (submittingRef.current || processing) return;
        submittingRef.current = true;
        setProcessing(true);
        setError(null);
        try {
            const {error: stripeError, setupIntent} = await stripe.confirmCardSetup(cardSetupSecret, {
                payment_method: {card: elements.getElement(CardElement), billing_details: {name: cardholderName || 'Card Holder'}},
            });
            if (stripeError) { setError(stripeError.message); }
            else if (setupIntent.status === 'succeeded') { await onSuccess(setupIntent.payment_method); onClose(); }
        } finally {
            submittingRef.current = false;
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Box sx={{mb: 2}}>
                <Typography sx={{fontWeight: 600, mb: 0.75, color: 'var(--brand-text-muted, #475569)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.6px'}}>Cardholder Name</Typography>
                <Box sx={{display: 'flex', alignItems: 'center', p: '11px 14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', backgroundColor: 'var(--brand-surface, #f8fafc)', '&:focus-within': {borderColor: ORANGE, boxShadow: `0 0 0 3px rgba(255,105,0,0.08)`, backgroundColor: 'var(--brand-surface, #fff)'}}}>
                    <input value={cardholderName} onChange={e => setCardholderName(e.target.value)} placeholder="Full name on card"
                        style={{border: 'none', outline: 'none', width: '100%', fontSize: '14px', color: 'var(--brand-text, #1e293b)', fontFamily: 'inherit', backgroundColor: 'transparent'}}/>
                </Box>
            </Box>
            <Box sx={{mb: 2.5}}>
                <Typography sx={{fontWeight: 600, mb: 0.75, color: 'var(--brand-text-muted, #475569)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.6px'}}>Card Number</Typography>
                <Box sx={{p: '13px 14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', backgroundColor: 'var(--brand-surface, #f8fafc)', '&:focus-within': {borderColor: ORANGE, backgroundColor: 'var(--brand-surface, #fff)'}}}>
                    <CardElement options={{style: {base: {fontSize: '14px', color: stripeTextColor, fontFamily: 'inherit', '::placeholder': {color: stripePlaceholderColor}, iconColor: '#FF6900'}}}}/>
                </Box>
            </Box>
            {error && (
                <Box sx={{mb: 2, p: 1.5, backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px'}}>
                    <Typography sx={{color: '#dc2626', fontSize: '0.8rem'}}>{error}</Typography>
                </Box>
            )}
            <Box sx={{display: 'flex', gap: 1.5, mt: 0.5}}>
                <Button type="button" variant="outlined" onClick={onClose} disabled={processing} fullWidth
                    sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#64748b', py: 1.25}}>
                    Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={!stripe || processing} fullWidth
                    sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 700, py: 1.25, backgroundColor: ORANGE, boxShadow: 'none', '&:hover': {backgroundColor: '#e55e00'}, '&.Mui-disabled': {backgroundColor: '#e2e8f0', boxShadow: 'none'}}}>
                    {processing ? 'Saving...' : 'Save Card'}
                </Button>
            </Box>
        </form>
    );
};

// ─── Payment Tab ─────────────────────────────────────────────────
const getCardBrandIcon = (brand) => {
    const brandMap = { visa: '💳', mastercard: '💳', amex: '💳', discover: '💳' };
    return brandMap[brand?.toLowerCase()] || '💳';
};

const PaymentTab = () => {
    const [savedCards, setSavedCards] = useState([]);
    const [cardsLoading, setCardsLoading] = useState(true);
    const [addCardOpen, setAddCardOpen] = useState(false);
    const [cardSetupSecret, setCardSetupSecret] = useState(null);
    const [publishableKey, setPublishableKey] = useState(null);
    const [removingCardId, setRemovingCardId] = useState(null);
    const [settingPrimaryCardId, setSettingPrimaryCardId] = useState(null);

    const stripePromise = React.useMemo(
        () => (publishableKey ? loadStripeJs(publishableKey) : null),
        [publishableKey]
    );

    const fetchCards = useCallback(async () => {
        setCardsLoading(true);
        try {
            const res = await listSavedCardsService();
            if (res?.data) {
                setSavedCards(res.data.payment_methods || []);
            }
        } catch (e) {
            console.error('Error fetching saved cards:', e);
        } finally {
            setCardsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCards();
        (async () => {
            try {
                const cfg = await getStripeConfigService();
                setPublishableKey(cfg?.data?.publishableKey || cfg?.publishableKey || null);
            } catch (_) {}
        })();
    }, [fetchCards]);

    const handleOpenAddCard = async () => {
        try {
            const res = await setupCardService();
            if (res?.data) { setCardSetupSecret(res.data.client_secret); setAddCardOpen(true); }
        } catch (_) {}
    };

    const handleSetPrimary = async (id) => {
        setSettingPrimaryCardId(id);
        try {
            await setPrimaryCardService(id);
            await fetchCards();
        } catch (_) {}
        setSettingPrimaryCardId(null);
    };

    const handleRemoveCard = async (id) => {
        setRemovingCardId(id);
        try {
            await removeCardService(id);
            await fetchCards();
        } catch (_) {}
        setRemovingCardId(null);
    };

    return (
        <Box>
            <PaymentMethodsTab
                cardsLoading={cardsLoading}
                savedCards={savedCards}
                handleOpenAddCard={handleOpenAddCard}
                handleSetPrimary={handleSetPrimary}
                handleRemoveCard={handleRemoveCard}
                settingPrimaryCardId={settingPrimaryCardId}
                removingCardId={removingCardId}
                getCardBrandIcon={getCardBrandIcon}
            />

            {/* Add card dialog */}
            {publishableKey && stripePromise && (
                <Dialog open={addCardOpen} onClose={() => setAddCardOpen(false)} maxWidth="sm" fullWidth
                    PaperProps={{sx: {borderRadius: '20px', overflow: 'hidden'}}}>
                    <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--brand-border, #f1f5f9)'}}>
                        <Box>
                            <Typography sx={{fontWeight: 800, fontSize: '1.1rem', color: 'var(--brand-text, #0f172a)'}}>Add New Card</Typography>
                            <Typography sx={{color: '#94a3b8', fontSize: '0.78rem', mt: 0.25}}>Your card details are encrypted and secure</Typography>
                        </Box>
                        <IconButton onClick={() => setAddCardOpen(false)} size="small"><CloseIcon/></IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Elements stripe={stripePromise}>
                            <AddCardForm
                                cardSetupSecret={cardSetupSecret}
                                onSuccess={async () => { setAddCardOpen(false); await fetchCards(); }}
                                onClose={() => setAddCardOpen(false)}
                            />
                        </Elements>
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
};

// ─── Credits Tab ─────────────────────────────────────────────────
const CreditsTab = () => {
    const upgradeClasses = useUpgradeStyles();
    const { isDark } = useColorMode();
    const [dataLoading, setDataLoading] = useState(true);
    const [creditData, setCreditData] = useState(null);
    // 'partner' | 'direct' — a partner's payment screen shows DQ Tokens only.
    const [channel, setChannel] = useState(null);
    const [planLabel, setPlanLabel] = useState('');
    const [planKey, setPlanKey] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
    const [periodEndDate, setPeriodEndDate] = useState('-');
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [cancelMode, setCancelMode] = useState('period_end');
    const [showPlans, setShowPlans] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [topUpDialogOpen, setTopUpDialogOpen] = useState(false);
    const [topUpAmount, setTopUpAmount] = useState(50);
    const [topUpClientSecret, setTopUpClientSecret] = useState(null);
    const location = useLocation();
    const history = useHistory();
    const [publishableKey, setPublishableKey] = useState(null);
    // Bumped after a DQ Token purchase so the DQT card re-reads its balance.
    const [walletKey, setWalletKey] = useState(0);
    // What the purchase panel is offering — drives the card's Top up shortcut.
    const [dqtOffers, setDqtOffers] = useState({ can_buy_plan: false, can_top_up: false });
    const dqtPurchaseRef = React.useRef(null);
    // The Checkout Session Stripe returned the buyer with (redirect-based payment methods).
    const dqtReturnSessionId = new URLSearchParams(location.search).get('dqt_checkout');

    const stripePromise = React.useMemo(
        () => (publishableKey ? loadStripeJs(publishableKey) : null),
        [publishableKey]
    );

    const applySubData = (d) => {
        setPlanLabel(d.plan_label || '');
        const key = d.plan_key || DQ_PRO_PLAN_KEY.DEFAULT;
        setPlanKey(key);
        setCancelAtPeriodEnd(d.cancel_at_period_end || false);
        setSubscriptionStatus(
            d.cancel_at_period_end && d.subscription_status === 'active'
                ? 'cancel_at_period_end'
                : d.subscription_status
        );
        if (d.subscription_renews_at) {
            const dt = new Date(d.subscription_renews_at);
            setPeriodEndDate(dt.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'}));
        }
    };

    useEffect(() => {
        const load = async () => {
            setDataLoading(true);
            try {
                const [subRes, creditRes, cfgRes, walletRes] = await Promise.all([
                    getSubscriptionStatusService(),
                    getCreditBalanceService(),
                    getStripeConfigService(),
                    getDqtWalletService().catch(() => null),
                ]);
                if (subRes?.data) applySubData(subRes.data);
                if (creditRes?.data) setCreditData(creditRes.data);
                setChannel(walletRes?.data?.channel || null);
                const pk = cfgRes?.data?.publishableKey || cfgRes?.publishableKey || null;
                setPublishableKey(pk);
            } catch (_) {}
            setDataLoading(false);
        };
        load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleConfirmCancel = async () => {
        setActionLoading(true);
        try {
            await cancelSubscriptionService(cancelMode === 'immediate');
            const res = await getSubscriptionStatusService();
            if (res?.data) applySubData(res.data);
        } catch (_) {}
        setActionLoading(false);
        setCancelModalOpen(false);
    };

    const handleResumeSubscription = async () => {
        setActionLoading(true);
        try {
            await resumeSubscriptionService();
            const res = await getSubscriptionStatusService();
            if (res?.data) applySubData(res.data);
        } catch (_) {}
        setActionLoading(false);
    };

    /**
     * Buying credits with money is retired: money buys DQ Tokens, and credits come
     * from converting them (the plan sets the price). The dialog and its endpoint
     * still work, so this is a flag rather than a deletion — flip it back if the
     * credit top-up is ever sold again.
     */
    const SHOW_CREDIT_TOPUP = false;

    /** Scroll to the DQ Token purchase panel — where credits now come from. */
    const scrollToDqtPurchase = () => {
        const el = dqtPurchaseRef.current;
        if (el) el.scrollIntoView({behavior: 'smooth', block: 'start'});
    };

    const handleOpenTopUp = () => {
        setTopUpAmount(50);
        setTopUpClientSecret(null);
        setTopUpDialogOpen(true);
    };

    // Deep-link: open the Top Up dialog directly when routed here with ?topup=1
    // (e.g. from the "Top up credits" prompt on the Edit Program dialog).
    useEffect(() => {
        if (new URLSearchParams(location.search).get('topup') !== '1') return;
        if (SHOW_CREDIT_TOPUP) {
            handleOpenTopUp();
        } else {
            // Credits are no longer bought directly — show where they come from.
            scrollToDqtPurchase();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const SHOW_CANCEL = process.env.REACT_APP_SHOW_CANCEL_SUBSCRIPTION !== 'false';
    const isActive = ['active', 'trialing', 'past_due', 'incomplete'].includes(subscriptionStatus);
    const balance = creditData?.balance_credits ?? 0;
    const balanceUsd = creditData?.balance_usd ?? 0;
    // A partner on a PbDQ plan holds DQ Tokens: its payment screen never shows credits.
    // Workspaces on older plans (default, educator, DQ Pro) still spend credits and see them.
    // A direct workspace (a school or network on a direct plan, 23 Sep 2026) sees only
    // credits: its balance is its organisation's pool, and its top-ups buy credits.
    const isDirect = channel === 'direct';
    const showCredits = isDirect; //!(channel === 'partner' && String(planKey || '').startsWith('enterprise'));
    // A partner's network or school works from the pool its partner assigned: it never
    // sees DQ Tokens nor a way to buy them (28 Sep 2026).
    const drawsPartnerPool = !isDirect && creditData?.pool?.owner_type === 'org';
    // The plan's name, shown on the first card. The API's label wins; the constant
    // covers a status response that carries only the key.
    const planName = planLabel || DQ_PRO_PLAN_KEY_VERBOSE[planKey] || '';
    const isLow = showCredits && creditData && balance < 20;
    const isEmpty = creditData && balance === 0;

    if (dataLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200}}>
                <CircularProgress style={{color: ORANGE}}/>
            </Box>
        );
    }

    const statusChip = (() => {
        const map = {
            active: {label: 'Active', bg: '#dcfce7', color: '#166534'},
            trialing: {label: 'Trial', bg: '#dbeafe', color: '#1e40af'},
            past_due: {label: 'Past Due', bg: '#fef9c3', color: '#854d0e'},
            cancel_at_period_end: {label: 'Cancelling', bg: '#fee2e2', color: '#991b1b'},
            cancelled: {label: 'Cancelled', bg: '#f1f5f9', color: '#64748b'},
        };
        return map[subscriptionStatus] || null;
    })();

    return (
        <Box>
            {/* Low balance warning */}
            {isLow && (
                <Box sx={{
                    mb: 2.5, p: '12px 18px', borderRadius: '12px',
                    // Low-alpha tint over the page background (light or dark) instead of a
                    // solid pastel hex — reads as a soft red/amber wash in both modes,
                    // same technique as the "Intro instructions" box below.
                    backgroundColor: isEmpty ? 'rgba(239,68,68,0.10)' : 'rgba(245,158,11,0.10)',
                    border: `1px solid ${isEmpty ? 'rgba(239,68,68,0.35)' : 'rgba(245,158,11,0.35)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                        <Typography sx={{fontSize: '1.1rem'}}>{isEmpty ? '🔴' : '⚠️'}</Typography>
                        <Box>
                            <Typography sx={{fontSize: '0.875rem', fontWeight: 700, color: isEmpty ? (isDark ? '#fca5a5' : '#991b1b') : (isDark ? '#fcd34d' : '#92400e')}}>
                                {isEmpty ? 'Out of Credits' : 'Low Credit Balance'}
                            </Typography>
                            <Typography sx={{fontSize: '0.775rem', color: isEmpty ? (isDark ? '#f3a8a8' : '#b91c1c') : (isDark ? '#fde68a' : '#a16207')}}>
                                {isEmpty
                                    ? 'Your balance is 0. Top up to continue creating programs or assessments.'
                                    : `Only ${balance} credits remaining. Consider topping up soon.`}
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="contained" size="small"
                        onClick={SHOW_CREDIT_TOPUP
                            ? handleOpenTopUp
                            : scrollToDqtPurchase}
                        sx={{textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', borderRadius: '8px',
                            backgroundColor: isEmpty ? '#ef4444' : '#f59e0b', color: '#fff', boxShadow: 'none',
                            '&:hover': {backgroundColor: isEmpty ? '#dc2626' : '#d97706', boxShadow: 'none'}}}>
                        {SHOW_CREDIT_TOPUP ? 'Top Up Now' : 'Get Credits'}
                    </Button>
                </Box>
            )}

            {/* Intro instructions — credits only (not a direct workspace: it assigns no licences to clients) */}
            {showCredits && !isDirect && <Box sx={{
                mb: 3,
                p: '16px 20px',
                borderRadius: '12px',
                backgroundColor: 'rgba(0,151,178,0.06)',
                border: '1px solid rgba(0,151,178,0.18)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
            }}>
                <InfoOutlinedIcon sx={{color: '#0097b2', fontSize: 20, mt: '2px', flexShrink: 0}}/>
                <Box>
                    <Typography sx={{fontSize: '0.9rem', fontWeight: 600, color: 'var(--brand-text, #111827)', lineHeight: 1.6}}>
                        The following shows how much credit you have.
                    </Typography>
                    <Typography sx={{fontSize: '0.9rem', color: 'var(--brand-text-muted, #6B7280)', lineHeight: 1.6, mt: 0.25}}>
                        Each user license you assign to your client uses <strong style={{color: '#0097b2'}}>3 credits</strong>.
                    </Typography>
                </Box>
            </Box>}

            {/* Two-column layout */}
            <Box sx={{display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'stretch'}}>

                {/* LEFT — Credit balance card (not for a partner, which holds DQ Tokens) */}
                {showCredits && <Box sx={{
                    width: '80%',
                    flex: '1 1 320px',
                    background: 'var(--brand-surface, #fff)', border: '1px solid var(--brand-border, #E5E7EB)',
                    borderRadius: '20px', p: '28px 28px 24px',
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                    position: 'relative', overflow: 'hidden',
                }}>
                    <Box sx={{position: 'absolute', top: 0, left: 0, right: 0, height: 4,
                        background: ORANGE, borderRadius: '20px 20px 0 0'}}/>

                    <Box>
                        <Typography sx={{fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF',
                            letterSpacing: '1.5px', textTransform: 'uppercase', mb: 1.25}}>
                            Credit Balance
                        </Typography>
                        {planName && (
                            <Typography sx={{fontSize: '0.85rem', fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 1.25}}>
                                {planName}
                            </Typography>
                        )}
                        <Box sx={{display: 'flex', alignItems: 'flex-end', gap: 1, mb: 0.5}}>
                            <Typography sx={{fontSize: 56, fontWeight: 900, color: '#0097b2', lineHeight: 1}}>
                                {Number(balance).toLocaleString('en-US')}
                            </Typography>
                            <Typography sx={{fontSize: 15, fontWeight: 700, color: '#0097b2',
                                textTransform: 'uppercase', mb: 0.6}}>
                                Credits
                            </Typography>
                        </Box>
                        {/* A pool's credits were not bought for dollars: the API sends no USD value. */}
                        {typeof balanceUsd === 'number' && creditData?.balance_usd !== null && (
                            <Typography sx={{fontSize: '0.825rem', color: '#94a3b8'}}>
                                ≈ <strong style={{color: 'var(--brand-text-muted, #475569)'}}>${balanceUsd} USD</strong>
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3.5, flexWrap: 'wrap', gap: 1.5}}>
                        <Typography sx={{fontSize: '0.75rem', color: '#9CA3AF', lineHeight: 1.5}}>
                            {SHOW_CREDIT_TOPUP
                                ? <>1 Credit = $1 USD<br/>Min. top-up $10</>
                                : isDirect
                                    ? <>Spent when you build<br/>and give learners access</>
                                    : <>Credits come from<br/>converting DQ Tokens</>}
                        </Typography>
                        {isDirect && dqtOffers.can_top_up && (
                            <Button variant="contained" onClick={scrollToDqtPurchase}
                                sx={{backgroundColor: ORANGE, color: '#fff', borderRadius: '10px',
                                    textTransform: 'none', fontWeight: 700, px: 3, py: 1, fontSize: '0.875rem',
                                    boxShadow: 'none', whiteSpace: 'nowrap',
                                    '&:hover': {backgroundColor: '#e55e00', boxShadow: '0 4px 12px rgba(255,105,0,0.25)'}}}>
                                Buy credits
                            </Button>
                        )}
                        {SHOW_CREDIT_TOPUP && (
                            <Button variant="contained" onClick={handleOpenTopUp}
                                sx={{backgroundColor: ORANGE, color: '#fff', borderRadius: '10px',
                                    textTransform: 'none', fontWeight: 700, px: 3, py: 1, fontSize: '0.875rem',
                                    boxShadow: 'none', whiteSpace: 'nowrap',
                                    '&:hover': {backgroundColor: '#e55e00', boxShadow: '0 4px 12px rgba(255,105,0,0.25)'}}}>
                                Top Up Credits
                            </Button>
                        )}
                    </Box>
                </Box>}

                {/* RIGHT — DQ Token (DQT) card. Renders nothing for a direct
                    workspace, which never sees DQT. */}
                <DqtWalletCard
                    key={walletKey}
                    onTopUp={dqtOffers.can_top_up ? scrollToDqtPurchase : null}
                    planName={planName}
                />
            </Box>

            {/* Buy the plan once, then top up DQ Tokens (Stripe Checkout, local
                currency); purchase history with invoices. Renders nothing when
                there is nothing to buy and no history. */}
            {/* Always mounted: it self-hides when there is nothing to buy, and
                otherwise explains why (no packs yet, plan not active, not the owner).
                The Stripe key is only needed once a checkout actually opens. */}
            {!drawsPartnerPool && (
                <DqtPurchasePanel
                    ref={dqtPurchaseRef}
                    onAvailability={setDqtOffers}
                    publishableKey={publishableKey}
                    returnSessionId={dqtReturnSessionId}
                    onReturnHandled={() => {
                        const params = new URLSearchParams(location.search);
                        params.delete('dqt_checkout');
                        history.replace({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' });
                    }}
                    onPurchased={() => {
                        setWalletKey((k) => k + 1);
                        getCreditBalanceService()
                            .then((r) => { if (r?.data) setCreditData(r.data); })
                            .catch(() => {});
                    }}
                />
            )}

            <CancelSubscriptionDialog
                open={cancelModalOpen}
                onClose={() => setCancelModalOpen(false)}
                cancelMode={cancelMode}
                setCancelMode={setCancelMode}
                periodEndDate={periodEndDate}
                handleConfirmCancel={handleConfirmCancel}
            />

            <PlansDialog
                open={showPlans}
                onClose={() => setShowPlans(false)}
                onSelectPlan={(selectedPlanKey) => {
                    setPlanKey(selectedPlanKey);
                    getSubscriptionStatusService().then(res => { if (res?.data) applySubData(res.data); });
                }}
                currentPlanKey={planKey}
                currentPlanTitle={planLabel}
                classes={upgradeClasses}
                forceNewSubscription={subscriptionStatus === 'cancelled'}
            />

            {/* Top Up dialog */}
            {SHOW_CREDIT_TOPUP && stripePromise && topUpDialogOpen && (
                <Elements stripe={stripePromise}>
                    <TopUpCreditsDialog
                        open={topUpDialogOpen}
                        onClose={() => { setTopUpDialogOpen(false); setTopUpClientSecret(null); }}
                        topUpAmount={topUpAmount}
                        setTopUpAmount={setTopUpAmount}
                        topUpClientSecret={topUpClientSecret}
                        setTopUpClientSecret={setTopUpClientSecret}
                        dqInsiderInfo={null}
                        creditData={creditData}
                        getCreditBalanceService={getCreditBalanceService}
                        setCreditData={setCreditData}
                        setLoading={setDataLoading}
                    />
                </Elements>
            )}

            <LoaderWithBackDrop loading={actionLoading}/>
        </Box>
    );
};

// ─── Licenses Tab (access level 21 only) ─────────────────────────
const LicensesTab = () => {
    const {authUser} = useAuth();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [assignLoading, setAssignLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [licenseData, setLicenseData] = useState(null); // { licenses_total, licenses_used, licenses_allocated }

    const fetchLicenses = useCallback(async () => {
        if (!authUser?.org_code) return;
        setDataLoading(true);
        try {
            const res = await getOrgDetailsService(authUser.org_code);
            const data = res?.data;
            const org = (data?.org && typeof data.org === 'object') ? data.org
                : (data?.data?.org && typeof data.data.org === 'object') ? data.data.org
                : null;
            if (org) {
                const lic = org.licenses_dq_assess || {};
                setLicenseData({
                    licenses_total: lic.total ?? 0,
                    licenses_used: lic.used ?? 0,
                    licenses_allocated: lic.allocated ?? 0,  // seats reserved across section sub-pools
                });
            }
        } catch (_) {}
        setDataLoading(false);
    }, [authUser?.org_code]);

    useEffect(() => { fetchLicenses(); }, [fetchLicenses]);

    const handleAssignLicenses = async ({org_code, license_count}) => {
        setAssignLoading(true);
        try {
            const response = await assignLicensesService(org_code, license_count);
            if (response?.data?.success || response?.data) {
                toast(TOAST_TYPES.SUCCESS, 'Licenses Added', 'Licenses have been successfully added to your account.');
                setDialogOpen(false);
                await fetchLicenses();
            }
        } catch (_) {
            toast(TOAST_TYPES.DANGER, 'Failed', 'Failed to add licenses. Please try again.');
        }
        setAssignLoading(false);
    };

    const total = licenseData?.licenses_total ?? 0;
    const used = licenseData?.licenses_used ?? 0;
    const allocated = licenseData?.licenses_allocated ?? 0;
    const remaining = Math.max(0, total - used);

    return (
        <Box>
            {/* ── Header card ── */}
            <Box sx={{
                border: '1px solid var(--brand-border, #E5E7EB)',
                borderRadius: '16px',
                backgroundColor: 'var(--brand-surface, #fff)',
                p: {xs: 3, sm: 4},
                mb: 3,
            }}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2}}>
                    <Box>
                        <Typography sx={{fontSize: '1.15rem', fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 0.5}}>
                            Licenses
                        </Typography>
                        <Typography sx={{fontSize: '0.875rem', color: 'var(--brand-text-muted, #6B7280)'}}>
                            Manage and add licenses for your organization.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={() => setDialogOpen(true)}
                        sx={{
                            backgroundColor: ORANGE,
                            color: '#fff',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            px: 3,
                            py: 1.1,
                            borderRadius: '10px',
                            boxShadow: 'none',
                            whiteSpace: 'nowrap',
                            '&:hover': {backgroundColor: '#e55e00', boxShadow: 'none'},
                        }}
                    >
                        + Add Licenses
                    </Button>
                </Box>
            </Box>

            {/* ── License usage card ── */}
            {dataLoading ? (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                    <CircularProgress style={{color: ORANGE}}/>
                </Box>
            ) : (
                <Box sx={{display: 'flex', gap: 2.5, flexWrap: 'wrap'}}>

                    {/* Total */}
                    <Box sx={{
                        flex: '1 1 160px',
                        border: '1px solid var(--brand-border, #E5E7EB)',
                        borderRadius: '16px',
                        backgroundColor: 'var(--brand-surface, #fff)',
                        p: '24px 28px',
                        position: 'relative', overflow: 'hidden',
                    }}>
                        <Box sx={{position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: ORANGE, borderRadius: '16px 16px 0 0'}}/>
                        <Typography sx={{fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', letterSpacing: '1.2px', textTransform: 'uppercase', mb: 1}}>
                            Total Licenses
                        </Typography>
                        <Typography sx={{fontSize: 44, fontWeight: 900, color: 'var(--brand-text, #111827)', lineHeight: 1}}>
                            {total}
                        </Typography>
                    </Box>

                    {/* Used */}
                    <Box sx={{
                        flex: '1 1 160px',
                        border: '1px solid var(--brand-border, #E5E7EB)',
                        borderRadius: '16px',
                        backgroundColor: 'var(--brand-surface, #fff)',
                        p: '24px 28px',
                        position: 'relative', overflow: 'hidden',
                    }}>
                        <Box sx={{position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#C91C6E', borderRadius: '16px 16px 0 0'}}/>
                        <Typography sx={{fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', letterSpacing: '1.2px', textTransform: 'uppercase', mb: 1}}>
                            Used
                        </Typography>
                        <Typography sx={{fontSize: 44, fontWeight: 900, color: 'var(--brand-text, #111827)', lineHeight: 1}}>
                            {used}
                        </Typography>
                    </Box>

                    {/* Allocated — seats reserved across class sub-pools */}
                    <Box sx={{
                        flex: '1 1 160px',
                        border: '1px solid var(--brand-border, #E5E7EB)',
                        borderRadius: '16px',
                        backgroundColor: 'var(--brand-surface, #fff)',
                        p: '24px 28px',
                        position: 'relative', overflow: 'hidden',
                    }}>
                        <Box sx={{position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#7C3AED', borderRadius: '16px 16px 0 0'}}/>
                        <Typography sx={{fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', letterSpacing: '1.2px', textTransform: 'uppercase', mb: 1}}>
                            Allocated
                        </Typography>
                        <Typography sx={{fontSize: 44, fontWeight: 900, color: 'var(--brand-text, #111827)', lineHeight: 1}}>
                            {allocated}
                        </Typography>
                    </Box>

                    {/* Remaining */}
                    <Box sx={{
                        flex: '1 1 160px',
                        border: '1px solid var(--brand-border, #E5E7EB)',
                        borderRadius: '16px',
                        backgroundColor: 'var(--brand-surface, #fff)',
                        p: '24px 28px',
                        position: 'relative', overflow: 'hidden',
                    }}>
                        <Box sx={{position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#0097b2', borderRadius: '16px 16px 0 0'}}/>
                        <Typography sx={{fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', letterSpacing: '1.2px', textTransform: 'uppercase', mb: 1}}>
                            Remaining
                        </Typography>
                        <Typography sx={{fontSize: 44, fontWeight: 900, color: 'var(--brand-text, #111827)', lineHeight: 1}}>
                            {remaining}
                        </Typography>
                    </Box>

                </Box>
            )}

            <AssignLicensesDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleAssignLicenses}
                loading={assignLoading}
                orgName={authUser?.org_name || authUser?.email || 'Your Organization'}
                orgCode={authUser?.org_code}
            />
        </Box>
    );
};

// ─── Features Tab ────────────────────────────────────────────────

const FeatureCard = ({ feature_key, label, is_active, source, actionLoading, onToggle }) => {
    const isActing = actionLoading === feature_key;
    const name = label || feature_key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2.5,
                py: 1.75,
                border: '1px solid',
                borderColor: is_active ? ORANGE : 'var(--brand-border, #E5E7EB)',
                borderRadius: '12px',
                backgroundColor: is_active ? 'rgba(255,105,0,0.06)' : 'var(--brand-surface, #ffffff)',
                transition: 'all 0.2s',
                gap: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Box sx={{
                    width: 34, height: 34, borderRadius: '8px', flexShrink: 0,
                    backgroundColor: is_active ? 'rgba(255,105,0,0.18)' : 'var(--brand-bg, #F3F4F6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {is_active
                        ? <CheckCircleIcon style={{ fontSize: 18, color: ORANGE }} />
                        : <CancelIcon style={{ fontSize: 18, color: 'var(--brand-text-muted, #9CA3AF)' }} />
                    }
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-text, #111827)' }}>
                            {name}
                        </Typography>
                        {source && (
                            <Typography sx={{
                                fontSize: 10, fontWeight: 600, px: 0.75, py: 0.25,
                                borderRadius: '4px', lineHeight: 1.6,
                                backgroundColor: is_active ? 'rgba(255,105,0,0.18)' : 'var(--brand-bg, #F3F4F6)',
                                color: is_active ? ORANGE : 'var(--brand-text-muted, #9CA3AF)',
                            }}>
                                {source.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            {!is_active && (
                <Box sx={{ flexShrink: 0 }}>
                    {isActing ? (
                        <CircularProgress size={20} style={{ color: ORANGE }} />
                    ) : (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => onToggle(feature_key, is_active)}
                            sx={{
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: 12,
                                minWidth: 95,
                                backgroundColor: ORANGE,
                                color: '#fff',
                                boxShadow: 'none',
                                '&:hover': { backgroundColor: '#e55a00', boxShadow: 'none' },
                            }}
                        >
                            Activate
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
};

const FeaturesTab = () => {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);

    const fetchFeatures = async () => {
        setLoading(true);
        try {
            const [availableRes, activeRes] = await Promise.all([
                getAvailableFeaturesService(),
                getActiveFeaturesService(),
            ]);
            const activeFeatures = activeRes?.data?.features || [];
            const availableFeatures = availableRes?.data?.features || [];
            const activeKeys = new Set(activeFeatures.map(f => f.feature_key));

            // Merge available + active; fall back to active-only if available is empty
            const base = availableFeatures.length > 0 ? availableFeatures : [];
            const baseKeys = new Set(base.map(f => f.feature_key));

            // Include any active features not already in the available list
            const extra = activeFeatures.filter(f => !baseKeys.has(f.feature_key));
            const merged = [
                ...base.map(f => ({ ...f, is_active: activeKeys.has(f.feature_key) })),
                ...extra.map(f => ({ ...f, is_active: true })),
            ];

            setFeatures(merged);
        } catch {
            toast(TOAST_TYPES.DANGER, 'Error', 'Failed to load features.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchFeatures(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleToggle = async (feature_key, is_active) => {
        setActionLoading(feature_key);
        try {
            if (is_active) {
                await deactivateFeatureService(feature_key);
            } else {
                await activateFeatureService(feature_key);
            }
            setFeatures(prev => prev.map(f =>
                f.feature_key === feature_key ? { ...f, is_active: !is_active } : f
            ));
        } catch {
            toast(TOAST_TYPES.DANGER, 'Error', `Failed to ${is_active ? 'deactivate' : 'activate'} feature.`);
        } finally {
            setActionLoading(null);
        }
    };

    const active = features.filter(f => f.is_active);
    const available = features.filter(f => !f.is_active);

    return (
        <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 0.5, color: 'var(--brand-text, #111827)' }}>
                Feature Access
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'var(--brand-text-muted, #6B7280)', mb: 3 }}>
                Manage features available on your plan. Activate or deactivate as needed.
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={32} style={{ color: ORANGE }} />
                </Box>
            ) : (
                <>
                    {active.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: ORANGE, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.25 }}>
                                Active ({active.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                {active.map(f => (
                                    <FeatureCard key={f.feature_key} {...f} actionLoading={actionLoading} onToggle={handleToggle} />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {available.length > 0 && (
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-text-muted, #9CA3AF)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.25 }}>
                                Available ({available.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                {available.map(f => (
                                    <FeatureCard key={f.feature_key} {...f} actionLoading={actionLoading} onToggle={handleToggle} />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {features.length === 0 && (
                        <Typography sx={{ color: 'var(--brand-text-muted, #9CA3AF)', fontSize: 14 }}>No features available.</Typography>
                    )}
                </>
            )}
        </Box>
    );
};

// ─── Main Page ───────────────────────────────────────────────────
// ─── Advanced Settings Tab ───────────────────────────────────────
/**
 * PW_PI_ST_AS — one control, deliberately.
 *
 * The Common Section deck's Advanced Settings slide contains a single item,
 * "Dark / Light mode toggle", and Ian confirmed on 20 Aug that nothing else
 * belongs here. Anything added later needs to come from the design, not from
 * whatever happens to be unplaced.
 */
const AdvancedSettingsTab = () => {
    const { isDark, toggleColorMode } = useColorMode();

    return (
        <Box sx={{maxWidth: 620}}>
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '10px', p: 2.5,
            }}>
                <Box>
                    <Typography sx={{fontSize: '0.95rem', fontWeight: 700, color: 'var(--brand-text, #111827)'}}>
                        Dark mode
                    </Typography>
                    <Typography sx={{fontSize: '0.85rem', color: 'var(--brand-text-muted, #6B7280)', mt: 0.5}}>
                        Switch between the light and dark appearance.
                    </Typography>
                </Box>
                <Switch
                    checked={isDark}
                    onChange={toggleColorMode}
                    inputProps={{'aria-label': 'Toggle dark mode'}}
                />
            </Box>
        </Box>
    );
};

const AccountSettings = () => {
    const {authUser} = useAuth();
    const { userCanViewAppearance } = useBranding();
    const [activeTab, setActiveTab] = useState(0);
    const location = useLocation();

    const isLevel21 = authUser?.access_level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;
    const isPaidUser = authUser?.dq_pro_plan?.status === 'active' && authUser?.dq_pro_plan?.plan_key !== DQ_PRO_PLAN_KEY.DEFAULT;
    const isP0 = authUser?.dq_pro_plan?.plan_key === DQ_PRO_PLAN_KEY.ENTERPRISE_P0;

    /**
     * A level-22 school. It INHERITS the workspace owner's dq_pro_plan, so every
     * billing figure on this page — subscription, payment method, credits,
     * invoices — belongs to the partner above it, not to the school.
     *
     * That is why /account/settings used to be blocked for level 22 outright.
     * The sheet gives School its own Settings (SW_PI_ST, rows 144-146) with
     * exactly three tabs — Account, Security, Advanced Settings, and pointedly
     * NO Billing and Payments — so the page opens for them with the owner's
     * commercial data withheld rather than the whole page withheld.
     */
    const isSchool = authUser?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;
    // LC Teacher (access 23) — the sheet's CS-PI-* rows give Teacher the same
    // bare three tabs as School (Account, Security, Advanced Settings), no
    // Billing and Payments — a teacher has no commercial relationship of
    // their own to show billing for, same reasoning as the isSchool case above.
    const isTeacher = authUser?.access_level === USER_ACCESS_LEVEL.TEACHER_ACCESS_DQ_INSIDER_PORTAL;
    // A school network under a partner sits at level 21, so `isSchool` misses it.
    // Like a school, it is billed through the partner, so it gets no billing tab.
    // A network that bought a direct plan for itself is its own owner and keeps it.
    const isPartnerNetwork = authUser?.is_school_network === true
        && authUser?.is_direct_workspace !== true;

    const tabLabels = useMemo(() => {
        // The four design tabs, minus the ones a school, partner network or teacher must not see.
        // Subscription is no longer a tab of its own, so the enterprise_p0
        // exemption moved onto the section inside Billing and Payments.
        const labels = TAB_LABELS_BASE.filter((l) => !((isSchool || isPartnerNetwork || isTeacher) && l === BILLING_TAB));
        if (userCanViewAppearance) labels.push(APPEARANCE_TAB);
        // Licenses removed per the SNW deck: the licence counts are already
        // shown in My Schools and My Classes. Level 21 is the only access level
        // that ever saw it. Commented out rather than deleted, like Features below.
        // if (isLevel21 && isPaidUser) labels.push(LICENSES_TAB);
        // Features activates workspace-level products — an owner action, and not
        // a tab the sheet gives School. Also hidden for enterprise_p0.
        // Temporarily removed for ALL access levels per request — commented
        // out rather than deleted so it's easy to bring back.
        // if (!isP0 && !isSchool) labels.push(FEATURES_TAB);
        return labels;
    }, [userCanViewAppearance, isLevel21, isPaidUser, isP0, isSchool, isPartnerNetwork, isTeacher]);

    useEffect(() => {
        if (activeTab >= tabLabels.length) setActiveTab(0);
    }, [activeTab, tabLabels.length]);

    // Deep-link ?topup=1 (e.g. from the Edit Program credit prompt): land on the
    // tab that now holds Credits so CreditsTab mounts and auto-opens its Top Up
    // dialog. Credits moved into Billing and Payments, so this targets that tab.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        // ?dqt_checkout=cs_... — Stripe returning the buyer from a DQ Token checkout;
        // the purchase panel inside Billing and Payments settles it.
        if (params.get('topup') === '1' || params.get('dqt_checkout')) {
            const idx = tabLabels.indexOf(BILLING_TAB);
            if (idx >= 0) setActiveTab(idx);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, tabLabels]);

    const activeLabel = tabLabels[activeTab];

    return (
        <Box sx={{minHeight: '88vh'}}>
            {/* Tab bar */}
            <Box sx={{borderBottom: '1px solid var(--brand-border, #E5E7EB)', mb: 3, display: 'flex', gap: 0}}>
                {tabLabels.map((label, i) => (
                    <Box
                        key={i}
                        onClick={() => setActiveTab(i)}
                        sx={{
                            px: 2, pb: 1.5, cursor: 'pointer',
                            borderBottom: activeTab === i ? '2px solid var(--brand-text, #111827)' : '2px solid transparent',
                            mr: 1,
                        }}
                    >
                        <Typography sx={{
                            fontSize: '0.95rem',
                            fontWeight: activeTab === i ? 700 : 500,
                            color: activeTab === i ? 'var(--brand-text, #111827)' : 'var(--brand-text-muted, #9CA3AF)',
                        }}>
                            {label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {activeLabel === 'Account' && <AccountTab/>}
            {activeLabel === 'Security' && <SecurityTab/>}
            {/*
              * Billing and Payments — subscription, payment method and credits
              * together, per Ian: "for credits, as in the mockup, we put all
              * related under Billing & Payments".
              *
              * The three existing components are rendered unchanged and stacked,
              * so nothing about the Stripe flows moves. Subscription keeps its
              * enterprise_p0 exemption, which used to hide the whole tab.
              */}
            {activeLabel === BILLING_TAB && (
                <Box>
                    {/*
                      * Each block was written as a standalone tab, so none of them
                      * carries a top margin — stacked, they butt straight up
                      * against the card above. The spacing lives here rather than
                      * inside the blocks so each still renders correctly on its
                      * own if it is ever used that way again.
                      */}
                    {/* Plan card temporarily removed per request — commented out
                        rather than deleted, same as its own "features" half above. */}
                    {false && !isP0 && <SubscriptionTab/>}
                    {/* Reordered per request: credit instructions + token balance
                        (CreditsTab) now come before Saved Cards (PaymentTab). */}
                    <CreditsTab/>
                    <Box sx={{mt: 4}}><PaymentTab/></Box>
                    {/*
                      * The former "Billing" sidebar item, now folded in here.
                      * Sheet row 18: "Billing — Move: inside 'Billing and
                      * Payments' under 'Settings'". It reads nothing from the URL,
                      * so it renders unchanged in this position.
                      *
                      * It opens on its own Billing details / Billing history tab
                      * bar. Left to itself that bar appears mid-page with nothing
                      * introducing it, reading as a stray control rather than the
                      * start of a section — hence the heading and rule above it.
                      */}
                    <Box sx={{mt: 5, pt: 4, borderTop: '1px solid var(--brand-border, #E5E7EB)'}}>
                        <DQProInvoiceList embedded/>
                    </Box>
                </Box>
            )}
            {/*
              * Advanced Settings — the dark/light toggle and nothing else.
              * Ian: "Only dark-light mode toggle should be under advanced
              * settings", and the deck's Advanced Settings slide draws exactly
              * that one control.
              */}
            {activeLabel === ADVANCED_TAB && <AdvancedSettingsTab/>}
            {activeLabel === APPEARANCE_TAB && <AppearanceTab/>}
            {activeLabel === LICENSES_TAB && <LicensesTab/>}
            {activeLabel === FEATURES_TAB && <FeaturesTab/>}
        </Box>
    );
};

export default AccountSettings;
